import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Container, Typography, Button, Box, Grid, TextField, Autocomplete,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";

import { PERMISSIONS } from "../../constants";
import { getAuditLogs } from "../../services/backend/ExpedientService";
import { exportAuditLogsToCSV } from "../../functions";

const AuditLog = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => state.user.users) || [];

  const [selectedUser, setSelectedUser] = useState(null);
  const [expedientId, setExpedientId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedUser) filters.userId = selectedUser.userId;
      if (expedientId.trim()) filters.expedientId = expedientId.trim();
      if (startDate) filters.startDate = startDate.startOf("day").toDate();
      if (endDate) filters.endDate = endDate.endOf("day").toDate();
      const response = await getAuditLogs(filters);
      setLogs(response || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user.permissions.includes(PERMISSIONS.AUDIT.LIST)) {
      navigate("/");
      return;
    }
    fetchLogs();
  }, []); // eslint-disable-line

  return (
    <Container maxWidth="lg" sx={{ p: 3, mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
          onClick={() => navigate("/")}
        >
          Volver
        </Button>
        Auditoría de carga de documentos
      </Typography>

      <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={users}
              value={selectedUser}
              onChange={(_, value) => setSelectedUser(value)}
              getOptionLabel={(o) => `${o.name || ""} ${o.lastName || ""}`.trim()}
              isOptionEqualToValue={(o, v) => o.userId === v.userId}
              renderInput={(params) => <TextField {...params} label="Usuario" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Expediente"
              value={expedientId}
              onChange={(e) => setExpedientId(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <DatePicker
              label="Desde"
              value={startDate}
              onChange={setStartDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <DatePicker
              label="Hasta"
              value={endDate}
              onChange={setEndDate}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={fetchLogs}
              disabled={loading}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Chip label={`Registros: ${logs.length}`} color="primary" />
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => exportAuditLogsToCSV(logs)}
            size="small"
            sx={{ ml: "auto" }}
            disabled={logs.length === 0}
          >
            Exportar CSV
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Fecha y hora</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Tipo documental</TableCell>
              <TableCell>Expediente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={28} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No se encontraron registros con los criterios seleccionados
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    {log.timestamp ? moment(log.timestamp).format("DD/MM/YYYY HH:mm:ss") : "—"}
                  </TableCell>
                  <TableCell>{log.userName || "—"}</TableCell>
                  <TableCell>{log.documentName || "—"}</TableCell>
                  <TableCell>{log.documentType || "—"}</TableCell>
                  <TableCell>{log.expedientId || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AuditLog;
